import logger from '../../lib/logger';
import React, { useState } from 'react';
import {
  Save, 
  FolderOpen, 
  Plus, 
  Undo2, 
  Redo2, 
  Move, 
  RotateCw, 
  Scaling,
  MousePointer2,
  Projector,
  Upload,
  Hand,
  Magnet,
  SaveAll,
  Thermometer,
  Box,
  Grid3x3
} from 'lucide-react';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '../ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useApp } from '../../context/AppContext';
import { toast } from 'sonner';
import FpsMeter from './FpsMeter';
import { useAuth } from '../../context/AuthContext';

const Toolbar = ({ onNewProject, onOpenProjects, onImportFile }) => {
  const { 
    currentProject, 
    sceneObjects, 
    projectors,
    setIsDirty,
    setLeftPanelOpen,
    setActiveLeftTab,
    transformMode,
    setTransformMode,
    gizmoMode,
    setGizmoMode,
    controlMode,
    setControlMode,
    saveProject,
    saveProjectAs,
    undo,
    redo,
    canUndo,
    canRedo,
    snapEnabled,
    setSnapEnabled,
    editMode,
    projectSettings,
    luxHeatmapEnabled,
    setLuxHeatmapEnabled,
    displayMode,
    setDisplayMode
  } = useApp();
  const { isAdmin } = useAuth();

  const [saveAsOpen, setSaveAsOpen] = useState(false);
  const [saveAsName, setSaveAsName] = useState('');

  const handleSave = async () => {
    try {
      await saveProject();
      setIsDirty(false);
      toast.success('Project saved');
    } catch (error) {
      logger.error('Save error:', error);
      toast.error('Failed to save project');
    }
  };

  const handleSaveAs = async () => {
    if (!saveAsName.trim()) {
      toast.error('Please enter a project name');
      return;
    }
    try {
      await saveProjectAs(saveAsName.trim());
      toast.success(`Project saved as "${saveAsName.trim()}"`);
      setSaveAsOpen(false);
      setSaveAsName('');
    } catch (error) {
      logger.error('Save As error:', error);
      toast.error('Failed to save project');
    }
  };

  const openProjectorLibrary = () => {
    setLeftPanelOpen(true);
    setActiveLeftTab('projectors');
  };

  return (
    <TooltipProvider>
      <div className="main-toolbar" data-testid="main-toolbar">
        {/* Logo */}
        <div className="flex items-center mr-3" data-testid="app-logo">
          <img
            src="/branding/avst-logo.png"
            alt="AV Simulation Tool"
            className="h-12 w-auto select-none"
            draggable={false}
            style={{ imageRendering: 'auto' }}
          />
        </div>

        <Separator orientation="vertical" className="h-6 bg-zinc-700" />

        {/* File operations */}
        <div className="flex items-center gap-1 ml-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8"
                onClick={onNewProject}
                data-testid="new-project-btn"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>New Project</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8"
                onClick={onOpenProjects}
                data-testid="open-project-btn"
              >
                <FolderOpen className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Open Project</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8"
                onClick={handleSave}
                data-testid="save-project-btn"
              >
                <Save className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Save Project (Ctrl+S)</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8"
                onClick={() => {
                  setSaveAsName((projectSettings?.name || currentProject?.name || 'Untitled') + ' - Copy');
                  setSaveAsOpen(true);
                }}
                data-testid="save-as-btn"
              >
                <SaveAll className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Save As (New Copy)</TooltipContent>
          </Tooltip>
        </div>

        <Separator orientation="vertical" className="h-6 bg-zinc-700 mx-2" />

        {/* Undo/Redo */}
        <div className="flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className={`h-8 w-8 ${!canUndo || editMode ? 'opacity-50' : ''}`}
                onClick={undo}
                disabled={!canUndo || editMode}
                data-testid="toolbar-undo-btn"
              >
                <Undo2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Undo (⌘Z){editMode ? ' - Disabled in Edit Mode' : ''}</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className={`h-8 w-8 ${!canRedo || editMode ? 'opacity-50' : ''}`}
                onClick={redo}
                disabled={!canRedo || editMode}
                data-testid="toolbar-redo-btn"
              >
                <Redo2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Redo (⇧⌘Z){editMode ? ' - Disabled in Edit Mode' : ''}</TooltipContent>
          </Tooltip>
        </div>

        <Separator orientation="vertical" className="h-6 bg-zinc-700 mx-2" />

        {/* Transform tools */}
        <div className="flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className={`h-8 w-8 ${transformMode === 'none' && controlMode === 'orbit' ? 'text-fuchsia-400 bg-fuchsia-500/20' : ''}`}
                onClick={() => { setTransformMode('none'); setGizmoMode('none'); setControlMode('orbit'); }}
                data-testid="select-tool-btn"
              >
                <MousePointer2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Select (No Transform)</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className={`h-8 w-8 ${transformMode === 'position' ? 'text-fuchsia-400 bg-fuchsia-500/20' : ''}`}
                onClick={() => { setTransformMode('position'); setGizmoMode('translate'); setControlMode('orbit'); }}
                data-testid="move-tool-btn"
              >
                <Move className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Move (G key)</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className={`h-8 w-8 ${transformMode === 'rotation' ? 'text-fuchsia-400 bg-fuchsia-500/20' : ''}`}
                onClick={() => { setTransformMode('rotation'); setGizmoMode('rotate'); setControlMode('orbit'); }}
                data-testid="rotate-tool-btn"
              >
                <RotateCw className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Rotate (R key)</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className={`h-8 w-8 ${transformMode === 'scale' ? 'text-fuchsia-400 bg-fuchsia-500/20' : ''}`}
                onClick={() => { setTransformMode('scale'); setGizmoMode('scale'); setControlMode('orbit'); }}
                data-testid="scale-tool-btn"
              >
                <Scaling className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Scale (S key)</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className={`h-8 w-8 ${controlMode === 'pan' ? 'text-fuchsia-400 bg-fuchsia-500/20' : ''}`}
                onClick={() => { setControlMode('pan'); setTransformMode('none'); }}
                data-testid="pan-tool-btn"
              >
                <Hand className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Pan View (Hand Tool)</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className={`h-8 w-8 ${snapEnabled ? 'text-fuchsia-400 bg-fuchsia-500/20' : ''}`}
                onClick={() => setSnapEnabled(!snapEnabled)}
                data-testid="snap-tool-btn"
              >
                <Magnet className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Snap to Grid ({snapEnabled ? 'ON' : 'OFF'})</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className={`h-8 w-8 ${luxHeatmapEnabled ? 'text-orange-400 bg-orange-500/20' : ''}`}
                onClick={() => setLuxHeatmapEnabled(!luxHeatmapEnabled)}
                data-testid="lux-heatmap-btn"
              >
                <Thermometer className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Lux Heatmap ({luxHeatmapEnabled ? 'ON' : 'OFF'})</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className={`h-8 w-8 ${displayMode === 'outline' ? 'text-cyan-300 bg-cyan-500/20' : ''}`}
                onClick={() => setDisplayMode(displayMode === 'outline' ? 'shaded' : 'outline')}
                data-testid="display-mode-btn"
              >
                {displayMode === 'outline' ? <Grid3x3 className="h-4 w-4" /> : <Box className="h-4 w-4" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              Display Mode: {displayMode === 'outline' ? 'Outline (wireframe)' : 'Shaded (textured)'} — click to toggle
            </TooltipContent>
          </Tooltip>
        </div>

        <Separator orientation="vertical" className="h-6 bg-zinc-700 mx-2" />

        {/* Quick add */}
        <div className="flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8"
                onClick={onImportFile}
                data-testid="import-file-btn"
              >
                <Upload className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Import 3D File (Drag & Drop also works)</TooltipContent>
          </Tooltip>
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Status */}
        <div className="flex items-center gap-3 text-xs text-zinc-400">
          {isAdmin && (
            <>
              <FpsMeter />
              <div className="w-px h-4 bg-zinc-700" />
            </>
          )}
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-green-500"></div>
            <span>Cloud Connected</span>
          </div>
        </div>
      </div>

      {/* Save As Dialog */}
      <Dialog open={saveAsOpen} onOpenChange={setSaveAsOpen}>
        <DialogContent className="sm:max-w-[400px] bg-zinc-900 border-zinc-700">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">Save As New Project</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label className="text-xs text-zinc-400 mb-2 block">Project Name</Label>
            <Input
              value={saveAsName}
              onChange={(e) => setSaveAsName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSaveAs(); }}
              placeholder="Enter project name..."
              className="bg-zinc-800 border-zinc-600 text-zinc-100"
              data-testid="save-as-name-input"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSaveAsOpen(false)} className="text-zinc-400">
              Cancel
            </Button>
            <Button onClick={handleSaveAs} className="bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="save-as-confirm-btn">
              Save As
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
};

export default Toolbar;
